import { useCallback, useMemo, useRef } from "react";
import { useAiEmailWizard } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/hooks/UseAiEmailWizard";
import { EMAIL_WIZARD_STEPS } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignConstants";
import type { AiCampaignForm, AiCampaignStepKey } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignTypes";
import type { CampaignDraftState } from "src/layouts/Engagements/Campaigns/shared/UseCampaignDraft";
import type { ChatCampaignSetupResult } from "./UseChatCampaignSetup";

export interface ChatWizardStepSnapshot {
  stepIndex: number;
  stepKey: AiCampaignStepKey;
  form: AiCampaignForm;
}

interface UseChatEmailCampaignWizardParams {
  campaignData: Partial<ChatCampaignSetupResult>;
  onAdvance: (_snapshot: ChatWizardStepSnapshot) => void;
  draftController: CampaignDraftState;
}

export const FIRST_WIZARD_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "productCategories");

export const useChatEmailCampaignWizard = ({
  campaignData,
  onAdvance,
  draftController,
}: UseChatEmailCampaignWizardParams) => {
  const aiWizardCampaignData = useMemo(
    () => ({
      campaignName: campaignData.campaignName ?? "",
      sendTo: campaignData.sendTo ?? [],
      excludeList: campaignData.excludeList ?? [],
      companyNameEngagements: campaignData.companyName ?? "",
    }),
    [campaignData.campaignName, campaignData.companyName, campaignData.excludeList, campaignData.sendTo]
  );

  const aiWizardBase = useAiEmailWizard(aiWizardCampaignData, {
    initialStepIndex: FIRST_WIZARD_STEP_INDEX,
    persist: false,
    draftController,
  });
  const { stepIndex, stepKey, form } = aiWizardBase;
  const latestStepIndexRef = useRef(stepIndex);
  latestStepIndexRef.current = stepIndex;

  const goNext = useCallback(async () => {
    const snapshot: ChatWizardStepSnapshot = { stepIndex, stepKey, form };
    const stepIndexBeforeAdvance = stepIndex;
    await aiWizardBase.goNext();
    if (latestStepIndexRef.current !== stepIndexBeforeAdvance) {
      onAdvance(snapshot);
    }
  }, [aiWizardBase, form, onAdvance, stepIndex, stepKey]);

  const { reset: resetBase } = aiWizardBase;
  const reset = useCallback(() => resetBase(aiWizardCampaignData), [aiWizardCampaignData, resetBase]);

  return useMemo(() => ({ ...aiWizardBase, goNext, reset }), [aiWizardBase, goNext, reset]);
};
